// @flow

import React from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { Link, ExpandableOverflow, MediaQuery } from '@riseart/common';
import { FilterService } from '@riseart/filter-service';
import { filter as ENUM_FILTER } from 'Enum';
import { I18nComponentsLoader } from 'shared_components/i18n/ComponentsLoader';
import { FilterLinks } from 'shared_components/common/filters/links/Links';
import { getFilterDomainConfig } from 'shared_models/configs/filters/domains';

import {
  raScreenMd,
  raScreenMdMax,
  raScreenLg,
} from '@riseart/antd-provider/dist/website/variables.less';

type Props = {
  domain: string,
  medium?: string,
  subMedium?: string,
  style?: string,
  subject?: string,
  subSubject?: string,
};

/**
 * FilterCategoriesLinks
 */
export function FilterCategoriesLinks({ domain, ...inputFilters }: Props): Node {
  const { formatMessage } = useIntl();
  const FILTER_DOMAIN_CONFIG = getFilterDomainConfig(ENUM_FILTER.domain.ART);
  const filterService = new FilterService(FILTER_DOMAIN_CONFIG, formatMessage);
  const mappedFilters =
    domain !== ENUM_FILTER.domain.ART
      ? FilterService.mapFilters(inputFilters, domain, ENUM_FILTER.domain.ART)
      : inputFilters;
  const similarLinks = filterService.getSimilarFilters(mappedFilters) || [];

  return (
    <MediaQuery minWidth={raScreenLg}>
      {(isDesktop) => (
        <MediaQuery minWidth={raScreenMd} maxWidth={raScreenMdMax}>
          {(isTablet) => (
            <I18nComponentsLoader dataKey="metaCategories">
              {({ art: whitelistedCategories }) => {
                const links = (
                  <FilterLinks
                    maxColumns={isTablet ? 3 : 4}
                    items={similarLinks.reduce((accumulator, { url, title }) => {
                      const totalItems = accumulator.length;

                      if (
                        whitelistedCategories.indexOf(
                          // whitelisted categories does not include the root path, so it has to be replaced
                          url.replace(`${FILTER_DOMAIN_CONFIG.rootUrlPath}/`, ''),
                        ) > -1 &&
                        ((isTablet && totalItems < 15) || (!isTablet && totalItems < 16))
                      ) {
                        return [
                          ...accumulator,
                          <Link type="light" to={url} title={title}>
                            {title}
                          </Link>,
                        ];
                      }

                      return accumulator;
                    }, [])}
                  />
                );

                return isTablet || isDesktop ? (
                  links
                ) : (
                  <ExpandableOverflow
                    maxHeight={160}
                    showMore={formatMessage({ id: 'common.show_more' })}
                    showLess={formatMessage({ id: 'common.show_less' })}
                  >
                    {links}
                  </ExpandableOverflow>
                );
              }}
            </I18nComponentsLoader>
          )}
        </MediaQuery>
      )}
    </MediaQuery>
  );
}
